import { ActiveOrganization, usePaymentAccount } from "@/api";
import { TypographySM } from "@/components/typography/SM";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PaymentAccountStatus } from "@/gql/__generated__/graphql";
import { cn } from "@/lib/utils";
import { TRIAL_DAYS } from "@/utils/subscriptionOfferTrialDays";
import React, { useEffect, useState } from "react";
import NoPaymentAccount from "./NoPaymentAccount";
import PaymentSectionSkeleton from "./PaymentSectionSkeleton";
import PriceOutput from "./PriceOutput";

const MAX_PRICE = 999_999;
const MIN_PRICE = 10;
interface Props {
  organization: ActiveOrganization;
  checkFormValidity: () => void;
}

const PaymentSection = ({ organization, checkFormValidity }: Props) => {
  const [price, setPrice] = useState<number | undefined>(10);
  const [priceError, setPriceError] = useState<string>();
  const [trialEnabled, setTrialEnabled] = useState(false);
  const { data, loading } = usePaymentAccount({
    organizationSlug: organization.slug
  });

  useEffect(() => {
    if (!data || data.organization.__typename !== "Organization") return;
    checkFormValidity();
  }, [checkFormValidity, data]);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPrice = e.target.value ? parseFloat(e.target.value) : undefined;
    if (Number.isNaN(newPrice)) return;

    setPrice(newPrice);
    if (newPrice === undefined) {
      setPriceError("Price is required");
    } else if (newPrice < MIN_PRICE) {
      setPriceError(`Minimum subscription fee is $${MIN_PRICE}`);
    } else if (newPrice > MAX_PRICE) {
      setPriceError(`Maximum subscription fee is $${MAX_PRICE}`);
    } else {
      setPriceError(undefined);
    }
  };

  const preventInvalidNumberInput = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (["+", "-", "e"].includes(e.key)) e.preventDefault();
  };

  const handleTrialToggle = (checked: boolean) => {
    setTrialEnabled(checked);
  };

  const hasPaymentAccount =
    data?.organization.__typename === "Organization" &&
    data.organization.paymentAccount?.status === PaymentAccountStatus.Verified;

  if (loading) return <PaymentSectionSkeleton />;
  if (!hasPaymentAccount)
    return <NoPaymentAccount organizationSlug={organization.slug} />;

  return (
    <>
      <div
        data-testid="payment-section"
        className="grid grid-cols-[30ch_1fr] gap-y-4"
      >
        <Label htmlFor="trial-toggle" className="self-center">
          Add {TRIAL_DAYS}-day free trial period:
        </Label>
        <Switch
          id="trial-toggle"
          name="trial-toggle"
          data-testid="trial-toggle"
          checked={trialEnabled}
          onCheckedChange={handleTrialToggle}
          className="focus-visible:ring-0.5 focus-visible:ring-offset-0.5"
        />

        <Label htmlFor="price" className="self-center">
          Monthly subscription fee:
        </Label>
        <div className="flex flex-col">
          <div className="relative">
            <TypographySM
              className={cn(
                "absolute inset-y-0 left-0 flex items-center pl-2",
                "font-light dark:font-extralight",
                price === undefined && "opacity-55"
              )}
            >
              $
            </TypographySM>
            <Input
              id="price"
              data-testid="price-input"
              name="price"
              type="number"
              min={MIN_PRICE}
              max={MAX_PRICE}
              required
              step="any"
              value={price ?? ""}
              onChange={handlePriceChange}
              onKeyDownCapture={preventInvalidNumberInput}
              placeholder="Enter price"
              className={cn(
                "pl-5",
                priceError && "border-red-500 focus-visible:ring-red-500"
              )}
            />
          </div>
          {priceError && (
            <TypographySM className="text-red-500 mt-1">
              {priceError}
            </TypographySM>
          )}
        </div>
      </div>

      <PriceOutput
        price={price}
        trialDays={trialEnabled ? TRIAL_DAYS : undefined}
      />
    </>
  );
};

export default PaymentSection;
